import { useState } from 'react';
import type { ActionConnection, ActionProviderInfo } from '../types';

interface Props {
  providers: ActionProviderInfo[];
  connections: ActionConnection[];
  busyId: string | null;
  onConnectForm: (provider: ActionProviderInfo) => void;
  onConnectOAuth: (provider: ActionProviderInfo) => void;
  onSaveConfig: (connection: ActionConnection, config: Record<string, string>) => void;
  onTest: (connection: ActionConnection) => void;
  onDelete: (connection: ActionConnection) => void;
}

function ConfigFields({
  provider,
  connection,
  busy,
  onSave,
}: {
  provider: ActionProviderInfo;
  connection: ActionConnection;
  busy: boolean;
  onSave: (config: Record<string, string>) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>(connection.config);
  const dirty = JSON.stringify(values) !== JSON.stringify(connection.config);

  if (!provider.configFields.length) return null;

  return (
    <div className="conn-config">
      {provider.configFields.map((f) => (
        <div className="form-row" key={f.key}>
          <label htmlFor={`cfg-${connection.id}-${f.key}`}>{f.label}</label>
          <input
            id={`cfg-${connection.id}-${f.key}`}
            type="text"
            placeholder={f.placeholder}
            value={values[f.key] ?? ''}
            onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
          />
          {f.help && <span className="help">{f.help}</span>}
        </div>
      ))}
      {dirty && (
        <button type="button" className="btn sm" disabled={busy} onClick={() => onSave(values)}>
          Save
        </button>
      )}
    </div>
  );
}

export function IntegrationsPanel({
  providers,
  connections,
  busyId,
  onConnectForm,
  onConnectOAuth,
  onSaveConfig,
  onTest,
  onDelete,
}: Props) {
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const connectionFor = (id: string) => connections.find((c) => c.provider === id);

  return (
    <div className="card">
      <div className="card-head">
        <h2>Integrations</h2>
      </div>
      <div className="card-body">
        <p className="help" style={{ marginBottom: 14 }}>
          Create a document straight from a task — the task's title, description, status, and a link back to its
          source go into the doc automatically.
        </p>
        <div className="conn-list">
          {providers.map((p) => {
            const connection = connectionFor(p.id);
            const busy = busyId === (connection?.id ?? p.id);
            return (
              <div key={p.id} className={`conn ${connection ? '' : 'disabled'}`}>
                <div className="body">
                  <div className="name">
                    {p.label}
                    {connection ? (
                      <span className="pill done">Connected</span>
                    ) : (
                      <span className="pill todo">Not connected</span>
                    )}
                  </div>
                  <div className="sub">{p.blurb}</div>
                  {connection && (
                    <ConfigFields
                      provider={p}
                      connection={connection}
                      busy={busy}
                      onSave={(config) => onSaveConfig(connection, config)}
                    />
                  )}
                  {confirmId === connection?.id && (
                    <div className="notice warn" style={{ marginTop: 8 }}>
                      <div>
                        Disconnect {p.label}? Documents already created stay where they are — this only stops new
                        ones.
                        <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                          <button className="btn danger sm" onClick={() => onDelete(connection)}>
                            Yes, disconnect
                          </button>
                          <button className="btn sm" onClick={() => setConfirmId(null)}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="actions">
                  {connection ? (
                    <>
                      <button className="btn sm" onClick={() => onTest(connection)} disabled={busy}>
                        {busy && <span className="spin" />}
                        Test
                      </button>
                      <button className="btn sm danger" onClick={() => setConfirmId(connection.id)} disabled={busy}>
                        Disconnect
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn sm primary"
                      disabled={busy}
                      onClick={() => (p.authMode === 'oauth' ? onConnectOAuth(p) : onConnectForm(p))}
                    >
                      {busy && <span className="spin" />}
                      {p.authMode === 'oauth' ? `Connect ${p.label}` : 'Connect'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
